import { redactText } from "./procurementSafeIssueReport";

const PUBLIC_PREFIXES = [
  "/p/",
  "/proposal/",
  "/proposal-manual/",
  "/project/",
  "/track/",
  "/portal-invoice/",
  "/clarity-one",
  "/articles/",
];

export function isCustomerFacingPublicPath(pathname: string): boolean {
  const path = (pathname.split("?")[0] ?? pathname).toLowerCase();
  if (path.startsWith("/clarity-one")) return true;
  return PUBLIC_PREFIXES.some((prefix) => path === prefix.slice(0, -1) || path.startsWith(prefix));
}

/** Same-origin path only; strip query, hash, and secret token segments. */
export function redactStaffRoute(raw: string | null | undefined): string {
  const s = String(raw ?? "").trim();
  const pathOnly = s.split("?")[0]?.split("#")[0] ?? "/";
  const withSlash = pathOnly.startsWith("/") ? pathOnly : `/${pathOnly}`;
  const parts = withSlash.split("/").map((segment, index, all) => {
    const prev = all[index - 1]?.toLowerCase();
    if (prev === "p" || prev === "track" || prev === "svr" || prev === "portal-invoice" || prev === "token") {
      return "[redacted]";
    }
    if (/^(?:[a-f0-9]{24,}|[A-Za-z0-9_-]{20,})$/.test(segment)) return "[redacted]";
    return segment.replace(/[^A-Za-z0-9_./:-]/g, "").slice(0, 64);
  });
  return parts.join("/").slice(0, 191) || "/";
}

export function normalizeSafeErrorCategory(error: unknown): string {
  if (!error) return "UNKNOWN";
  const anyErr = error as {
    data?: { code?: string; httpStatus?: number };
    shape?: { data?: { code?: string; httpStatus?: number } };
    message?: string;
    name?: string;
  };
  const code = String(anyErr.data?.code ?? anyErr.shape?.data?.code ?? "").trim();
  if (code) return code.replace(/[^A-Za-z0-9_]/g, "").slice(0, 64) || "UNKNOWN";
  const msg = String(anyErr.message ?? anyErr.name ?? "UNKNOWN");
  if (/unauthorized/i.test(msg)) return "UNAUTHORIZED";
  if (/forbidden/i.test(msg)) return "FORBIDDEN";
  if (/timeout|timed out/i.test(msg)) return "TIMEOUT";
  if (/network|fetch/i.test(msg)) return "NETWORK";
  return "INTERNAL_SERVER_ERROR";
}

export function extractHttpStatus(error: unknown): number | null {
  const anyErr = error as { data?: { httpStatus?: number }; shape?: { data?: { httpStatus?: number } } };
  const n = Number(anyErr.data?.httpStatus ?? anyErr.shape?.data?.httpStatus);
  return Number.isInteger(n) && n >= 100 && n < 600 ? n : null;
}

export function extractTraceId(error: unknown): string | null {
  const anyErr = error as { data?: { traceId?: string }; shape?: { data?: { traceId?: string } } };
  const raw = String(anyErr.data?.traceId ?? anyErr.shape?.data?.traceId ?? "").trim();
  if (!raw) return null;
  return raw.replace(/[^A-Za-z0-9_-]/g, "").slice(0, 64) || null;
}

export function singaporeTimestamp(now = new Date()): string {
  return new Intl.DateTimeFormat("en-SG", {
    timeZone: "Asia/Singapore",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(now);
}

export function sanitizeBrowserPlatform(raw: string | null | undefined): string {
  return redactText(raw, 180).replace(/https?:\/\/\S+/gi, "[redacted]").slice(0, 180);
}

export function sanitizeStackSnippet(raw: string | null | undefined): string {
  return redactText(raw, 400);
}

export type StaffForegroundIssueNoteInput = {
  incidentId: string;
  staffName?: string | null;
  area?: string | null;
  operation?: string | null;
  httpStatus?: number | null;
  traceId?: string | null;
  singaporeAt?: string | null;
  browser?: string | null;
  navigationContext?: string | null;
  retryAttempted?: boolean;
  stackSnippet?: string | null;
};

export function buildStaffForegroundIssueNote(input: StaffForegroundIssueNoteInput): string {
  const lines = [
    `incident=${String(input.incidentId).replace(/[^A-Za-z0-9_-]/g, "").slice(0, 64)}`,
    input.staffName ? `staff=${redactText(input.staffName, 80)}` : null,
    input.area ? `area=${redactText(input.area, 64)}` : null,
    input.operation ? `opName=${redactText(input.operation, 128)}` : null,
    input.httpStatus != null ? `http=${input.httpStatus}` : null,
    input.traceId ? `trace=${input.traceId}` : null,
    input.singaporeAt ? `sgt=${redactText(input.singaporeAt, 64)}` : null,
    input.browser ? `ua=${sanitizeBrowserPlatform(input.browser)}` : null,
    input.navigationContext ? `nav=${redactStaffRoute(input.navigationContext)}` : null,
    `retryAttempted=${input.retryAttempted ? "yes" : "no"}`,
    input.stackSnippet ? `stack=${sanitizeStackSnippet(input.stackSnippet)}` : null,
  ].filter(Boolean);
  return lines.join(" | ").slice(0, 1800);
}

export function applicationAreaFromRoute(route: string): string {
  const path = redactStaffRoute(route).toLowerCase();
  if (path.startsWith("/procurement")) return "procurement";
  if (path.startsWith("/admin/activity") || path.startsWith("/sales/activity")) return "activity";
  if (path.startsWith("/sales/customers") || path.startsWith("/customers")) return "customer_card";
  if (path.startsWith("/sales/proposals") || path.includes("/deal")) return "proposals";
  if (path.startsWith("/finance") || path.includes("invoice")) return "finance";
  if (path.includes("revenue") || path.includes("revcal")) return "revenue_calendar";
  if (path === "/" || path.startsWith("/sales")) return "sales_dashboard";
  return "staff_portal";
}
